import { resolveColour, type ColourRoleName, type CreatureRank, type NpcPlate, type Temperament } from "odyssey-shared";
import type { NameplateChoices } from "../../settings.ts";
import type { BarToneName } from "../health-bars/creature-bar.ts";

/**
 * What a creature's nameplate shows (R-0089 and R-0094, ADR-082's notes of 2026-10-02, the design's NPC and creature
 * nameplates): its attitude, from its template's temperament, as the design's colour and an 8 px mark, so colour never
 * says it alone (ADR-061); its level as a muted number before its name, as the design's "8 Marsh Stalker"; the form the
 * viewer's Nameplates choices give it; the rank frame its rank asks for; and when its health bar shows and in which
 * look. All of it is pure and allocates nothing on a frame: the level lines are made once each and the tints once.
 */

/**
 * How a nameplate's subject meets the player: a hostile creature attacks on sight, a neutral one only when struck; a
 * friendly NPC and a non-combatant (`passive`) NPC never fight. A creature takes the first two; an NPC (ADR-136) takes
 * the one its look's plate colour names (`attitudeOfPlate`).
 */
export const ATTITUDES = ["hostile", "neutral", "friendly", "passive"] as const;
export type Attitude = (typeof ATTITUDES)[number];

/**
 * One attitude's style: the colour role its name and mark take, its mark in the UI atlas, and its word (drafts for the
 * author).
 */
export interface AttitudeStyle {
  readonly role: ColourRoleName;
  readonly mark: string;
  readonly word: string;
}

/**
 * The attitudes' styles, the design's colours (the author's ruling of 2026-10-02): hostile `danger`, neutral `warning`
 * and friendly `positive`; a non-combatant NPC, which the design does not draw, takes the body text colour, the
 * author's white. Each mark is original art under `ui/art/marks/attitude/` in the same colour, outlined in ink as the
 * flag marks are; the design draws no attitude mark on a creature, and the mark stays so colour never says it alone.
 */
export const ATTITUDE_STYLES: Readonly<Record<Attitude, AttitudeStyle>> = Object.freeze({
  hostile: Object.freeze({ role: "danger", mark: "ui/attitude/hostile_8", word: "Hostile" }),
  neutral: Object.freeze({ role: "warning", mark: "ui/attitude/neutral_8", word: "Neutral" }),
  friendly: Object.freeze({ role: "positive", mark: "ui/attitude/friendly_8", word: "Friendly" }),
  passive: Object.freeze({ role: "text", mark: "ui/attitude/passive_8", word: "Non-combatant" }),
});

/**
 * The attitudes' tints, made once.
 */
const TINTS: Readonly<Record<Attitude, number>> = Object.freeze(Object.fromEntries(ATTITUDES.map((attitude) => [attitude, Number.parseInt(resolveColour(ATTITUDE_STYLES[attitude].role).slice(1), 16)])) as Record<Attitude, number>);

/**
 * The level lines of every level the wire's byte carries, made once.
 */
const LEVEL_LINES: readonly string[] = Object.freeze(Array.from({ length: 256 }, (_, level) => `${level}`));

/**
 * A creature's form on the viewer's Nameplates choices: the whole plate, the mark alone, or none.
 */
export type CreaturePlateForm = "full" | "mark" | "none";

/**
 * A creature's attitude from its template's temperament.
 *
 * @param temperament The temperament its look carries.
 * @returns Hostile for an aggressive creature, neutral for a neutral one.
 */
export function attitudeOf(temperament: Temperament): Attitude {
  return temperament === "aggressive" ? "hostile" : "neutral";
}

/**
 * An NPC's attitude from its look's plate colour (ADR-136 decision 2.4): white a non-combatant, gold a neutral, red a
 * hostile and green a friendly fighter.
 *
 * @param plate The plate colour its look carries.
 * @returns The attitude.
 */
export function attitudeOfPlate(plate: NpcPlate): Attitude {
  return plate === "white" ? "passive" : plate === "gold" ? "neutral" : plate === "red" ? "hostile" : "friendly";
}

/**
 * Whether an NPC's look carries a health bar and a level on its plate: every plate but a non-combatant's, whose
 * Health and Level on the wire are constants the page ignores (ADR-136's note on the map server).
 *
 * @param plate The plate colour its look carries.
 * @returns True for an NPC that fights.
 */
export function npcFights(plate: NpcPlate): boolean {
  return plate !== "white";
}

/**
 * An attitude's colour as a tint.
 *
 * @param attitude The attitude.
 * @returns The tint.
 */
export function attitudeTint(attitude: Attitude): number {
  return TINTS[attitude];
}

/**
 * The level line a plate draws before the name.
 *
 * @param level The Level component.
 * @returns The number, the same string each time for a level the wire's byte carries.
 */
export function levelLine(level: number): string {
  return LEVEL_LINES[level] ?? `${level}`;
}

/**
 * A creature's plate form. Creatures follow other players' plates, the choice that names what the viewer sees of
 * others: either name choice draws the whole plate, Flag only the attitude mark alone, as a player's plate draws its
 * flag mark alone, and Off nothing. The player's own plate choice has no say.
 *
 * @param choices The viewer's Nameplates choices.
 * @returns The form.
 */
export function creaturePlateForm(choices: Readonly<NameplateChoices>): CreaturePlateForm {
  if (choices.others === "off") {
    return "none";
  }
  return choices.others === "flag" ? "mark" : "full";
}

/**
 * Logical pixels between the level and the name: the pixel face's space at its native size, the design's one space.
 */
export const LEVEL_GAP = 2;

/**
 * The width of a plate's text beside its mark: the level line, the gap and the name in the whole plate, the level
 * line alone for a template with no name, and nothing for the mark alone.
 *
 * @param form The plate's form, one that draws a plate.
 * @param nameWidth The name's width in logical pixels, 0 for no name.
 * @param levelWidth The level line's width in logical pixels.
 * @returns The width in logical pixels.
 */
export function creatureTextWidth(form: Exclude<CreaturePlateForm, "none">, nameWidth: number, levelWidth: number): number {
  if (form === "mark") {
    return 0;
  }
  return nameWidth > 0 ? levelWidth + LEVEL_GAP + nameWidth : levelWidth;
}

/**
 * Where the name starts, measured from where the level line starts.
 *
 * @param levelWidth The level line's width in logical pixels.
 * @returns The offset in logical pixels.
 */
export function nameOffset(levelWidth: number): number {
  return levelWidth + LEVEL_GAP;
}

/**
 * The width of the frame a rank draws around the level, in logical pixels (the design: 1 for an elite, 2 for a boss).
 * No creature has a rank yet, so nothing draws it; the width is the hook the frame lands on.
 *
 * @param rank The rank its look carries.
 * @returns 0, 1 or 2.
 */
export function rankFrame(rank: CreatureRank): number {
  return rank === "boss" ? 2 : rank === "elite" ? 1 : 0;
}

/**
 * Whether a creature's health bar shows: under a plate drawn in some form, alive, and either damaged (the design:
 * hostile creatures show a bar only once damaged) or the player's target. A creature walks home and is healed to
 * full there, so its bar goes as it heals home, with no timer.
 *
 * @param health The Health component's percentage.
 * @param targeted Whether the creature is the player's target.
 * @param choices The viewer's Nameplates choices.
 * @returns True when the bar shows.
 */
export function creatureBarShown(health: number, targeted: boolean, choices: Readonly<NameplateChoices>): boolean {
  return health > 0 && (health < 100 || targeted) && creaturePlateForm(choices) !== "none";
}

/**
 * A creature's health bar look by its attitude (the design's `NpcNameplate`): a hostile creature's ringed bar in the
 * health colour, any other's plain green one.
 *
 * @param attitude The attitude.
 * @returns The bar's tone.
 */
export function barTone(attitude: Attitude): BarToneName {
  return attitude === "hostile" ? "hostile" : "calm";
}
